import { useEffect, useMemo, useState } from 'react';
// GAP: no DS icon for an unselected option (Circle); lucide in both modes (design sheet §3.1, logged not substituted).
import { ChevronDown, Check, Circle, Search, X } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  Avatar as SignalAvatar,
  TextField,
} from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { cn } from '@/lib/utils';
import type { MacroCollaborator } from '@/types/settings-api';

/**
 * Macro collaborator picker — PROD-764 PR-C.
 *
 * Multi-select Popover that lets a macro owner add/remove collaborators
 * from the macro. The picker itself is **pure UI**: the search query is
 * surfaced through the `onSearchChange` callback so the parent owns the
 * fetch. This keeps the hook side decoupled from the UI side (the hook
 * lands in a follow-up commit once a non-empty cURL pins the wire shape
 * — see `docs/CONVERSION_NOTES_macro_collaborator_picker.md`).
 *
 * **Flutter source:**
 *   - `collaborator_popup.dart` — the popup itself
 *   - `macro_tab.dart:1634-1672` — the dropdown shell that invokes it
 *
 * **UX parity (from Flutter):**
 *   - Search input at top with magnifier + clear-X icon
 *   - 500 ms debounce on search → parent re-fetches
 *   - Thin progress bar while loading
 *   - 24px avatar + name per row; alternating row background
 *   - Selection: `check_circle` (selected) / `radio_button_unchecked` (off)
 *   - Multi-select; selected list lifted to the parent via onChange
 *
 * **Selection identity:**
 *   Two `MacroCollaborator` rows are considered the same person if their
 *   `collaboratorUserId` matches. The single `MacroCollaborator` model is
 *   reused across two contexts:
 *     - As a row on a macro (from `getMacrosByProviderId`) — `id` is the
 *       macro_collaborators table row-id, `macroId` is populated.
 *     - As a candidate from search (from `GetProviderAndNurseList`) — `id`
 *       is the user-side identifier, `macroId` is empty.
 *   When a candidate is selected and the macro is saved, the dialog
 *   mirrors Flutter `macro_tab.dart:998` and overrides `macroId` on each
 *   selected row with the current macro's id; everything else (id,
 *   collaboratorUserId, name, photo) passes through unchanged. The
 *   backend reconciles row-id vs user-id server-side. `collaboratorUserId`
 *   is the stable selection key.
 *
 * **Permission gate:**
 *   The parent controls `disabled` based on `canAddCollaborators()` —
 *   read from the practitioner permissions. PR-C ships with the prop
 *   wired through but the calling site (macro-edit-dialog) just defaults
 *   to `false` until the permissions API lands.
 */
export interface MacroCollaboratorPickerProps {
  /**
   * Currently-selected collaborators on the macro. These render as the
   * "selected" rows in the popup plus the chip count on the trigger.
   */
  selected: MacroCollaborator[];
  /**
   * Full candidate list returned by the parent's search hook (already
   * filtered server-side by the debounced `searchTerm`). Empty array
   * means "no results".
   */
  candidates: MacroCollaborator[];
  /** True while the parent's fetch is in flight. Drives the loading bar. */
  isLoading?: boolean;
  /**
   * Fires on every search input change (NOT debounced — the parent is
   * expected to debounce before hitting the API, mirroring Flutter's
   * 500 ms `_debounce` Timer pattern).
   */
  onSearchChange?: (next: string) => void;
  /** Fires when the selection set changes. Receives the new full list. */
  onChange: (next: MacroCollaborator[]) => void;
  /**
   * Disables the trigger entirely. Used by the parent to apply the
   * permission gate (`canAddCollaborators` in Flutter terms).
   */
  disabled?: boolean;
}

export function MacroCollaboratorPicker({
  selected,
  candidates,
  isLoading,
  onSearchChange,
  onChange,
  disabled,
}: MacroCollaboratorPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);

  // Bubble debounced search up to the parent. The parent re-fetches on
  // change; the input itself is uncontrolled below the debounce.
  useEffect(() => {
    onSearchChange?.(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const selectedKeys = useMemo(
    () => new Set(selected.map((c) => c.collaboratorUserId)),
    [selected],
  );

  /**
   * Merge selected + candidates for the popover list, dedup by
   * collaboratorUserId, sort selected to the top.
   *
   * Without this, a user editing an existing macro could see their
   * existing collaborators as chips below the trigger but NOT see them
   * in the popover (the popover only renders `candidates` from the
   * search hook, and the user's current collaborators may not match
   * the empty-search default response). That meant the only way to
   * uncheck a current collaborator was the chip's X button. Surfacing
   * them in the popover too is the canonical multi-select UX.
   */
  const popoverRows = useMemo(() => {
    const candidateKeys = new Set(
      candidates.map((c) => c.collaboratorUserId),
    );
    const selectedNotInCandidates = selected.filter(
      (s) => !candidateKeys.has(s.collaboratorUserId),
    );
    return [...selectedNotInCandidates, ...candidates];
  }, [selected, candidates]);

  const toggleCollaborator = (c: MacroCollaborator) => {
    const isSelected = selectedKeys.has(c.collaboratorUserId);
    if (isSelected) {
      onChange(
        selected.filter((s) => s.collaboratorUserId !== c.collaboratorUserId),
      );
    } else {
      onChange([...selected, c]);
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    onSearchChange?.('');
  };

  return (
    <div>
      <FieldLabel stacked>Collaborators</FieldLabel>
      <AnchoredPopover open={open} onOpenChange={setOpen}>
        {/* base-ui's PopoverTrigger uses a `render` prop, not Radix's
            `asChild`. Without this the trigger generates its own
            <button> wrapping mine — nested buttons swallow the click
            and the popover never opens. Matches the canonical pattern
            used in src/components/ui/popover.stories.tsx:29. */}
        <AnchoredPopoverTrigger
          render={(props) => (
            <button
              {...props}
              type="button"
              disabled={disabled}
              className={cn(
                'mt-1 flex w-full items-center justify-between rounded-md border border-border-hairline-strong bg-surface-card px-3 py-2 text-left text-sm',
                'hover:border-brand-primary/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/20',
                disabled && 'opacity-50 cursor-not-allowed hover:border-border-hairline-strong',
              )}
              aria-label="Edit macro collaborators"
            >
              <span className="truncate">
                {selected.length === 0 ? (
                  <span className="text-text-muted">Add collaborators…</span>
                ) : (
                  <span className="text-text-default">
                    {selected.length} collaborator
                    {selected.length === 1 ? '' : 's'} selected
                  </span>
                )}
              </span>
              <ChevronDown className="size-4 text-text-secondary flex-shrink-0 ml-2" />
            </button>
          )}
        />
        <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }}
          align="start"
          className="w-anchor p-0 max-h-85 overflow-hidden gap-3"
        >
          {/* Search row — Flutter parity: magnifier + clear-X */}
          <div className="border-b border-border-hairline p-2">
            <div className="flex items-center gap-2 rounded-md bg-grey-100 px-3">
              <Search className="size-4 text-text-secondary flex-shrink-0" />
              <TextField
                value={searchInput}
                placeholder="Search"
                onChange={(e) => setSearchInput(e.target.value)}
                inputClassName="border-none bg-transparent px-0 h-10 text-sm focus-visible:ring-0"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                  className="icon-action"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
          </div>

          {/* Loading bar — thin 3px linear, brand-primary on grey */}
          {isLoading ? (
            <div className="relative h-0.75 bg-blue-100 overflow-hidden">
              <div className="absolute inset-y-0 left-0 w-1/3 bg-brand-primary animate-pulse" />
            </div>
          ) : null}

          {/* Results list — selected collaborators are surfaced at the
              top even when the search hook hasn't returned them, so
              the user can uncheck currently-selected entries from
              inside the popover (not just via the chip X). */}
          <ul className="max-h-60 overflow-y-auto scrollbar-hide">
            {popoverRows.length === 0 && !isLoading ? (
              <li className="px-3 py-4 text-center text-sm text-text-muted">
                {searchInput
                  ? `No matches for "${searchInput}"`
                  : 'Type to search providers and nurses'}
              </li>
            ) : (
              popoverRows.map((c, i) => {
                const isSelected = selectedKeys.has(c.collaboratorUserId);
                return (
                  <li key={c.collaboratorUserId || c.id || i}>
                    <button
                      type="button"
                      onClick={() => toggleCollaborator(c)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm',
                        // Alternating row background — Flutter parity
                        i % 2 === 0 ? 'bg-surface-card' : 'bg-grey-100/50',
                        'hover:bg-blue-100/50',
                      )}
                    >
                      {isSelected ? <Check className="size-4 text-text-secondary flex-shrink-0" /> : (
                        <Circle className="size-4 text-text-secondary flex-shrink-0" />
                      )}
                      <SignalAvatar
                        size="xs"
                        src={c.collaboratorPhotoUrl ?? undefined}
                        name={c.collaboratorName ?? undefined}
                        initials={initialsOf(c.collaboratorName)}
                        alt={c.collaboratorName ?? undefined}
                      />
                      <span className="flex-1 truncate font-medium text-text-default">
                        {c.collaboratorName || '—'}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </AnchoredPopoverContent>
      </AnchoredPopover>

      {/* Selected-collaborators chip row — when populated, render below
          the trigger so the user sees who's currently on the macro.

          TODO(PROD-1045): remove this hand-rolled pill and use Signal
          `CollaboratorChip` when it ships in signal-ds (DS-037 — Avatar +
          CollaboratorChip; spec-only as of 1.6.0, component not built yet).
          NOT migrated to <Tag onRemove> today because Tag is a fixed
          h-5 (20px), rounded-[4px], 12px-text *label* chip with only
          ps-2/pe-1 padding (see Tag.tsx) — it has no room for a leading
          round avatar, and its rounded-square shape + hover bg/border
          restyle clash with this rounded-full avatar pill. Dropping a
          16px rounded-full Avatar into a 20px square chip looks broken
          (avatar nearly fills the height, shape mismatch). Kept hand-rolled
          until Signal ships an avatar/person chip variant (PROD-1045). */}
      {selected.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {selected.map((c) => (
            <li
              key={c.collaboratorUserId || c.id}
              data-slot="macro-collab-chip"
              className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700"
            >
              <SignalAvatar
                size="2xs"
                src={c.collaboratorPhotoUrl ?? undefined}
                name={c.collaboratorName ?? undefined}
                initials={initialsOf(c.collaboratorName)}
                alt={c.collaboratorName ?? undefined}
              />
              <span>{c.collaboratorName}</span>
              <button
                type="button"
                onClick={() =>
                  onChange(
                    selected.filter(
                      (s) => s.collaboratorUserId !== c.collaboratorUserId,
                    ),
                  )
                }
                aria-label={`Remove ${c.collaboratorName}`}
                className="text-blue-700 hover:text-blue-900"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
